/**
 * The agent display types the dock renders as cards or step details: their
 * payloads read the way the backend's toolkit writes them
 * (`cleanse_api/copilot/toolkits/gtm_agents.py`).
 */
import type { ProposalReviewState } from '@/containers/agents/play-proposal-review.util';
import {
  parseWorkspaceSearch,
  searchOf,
  type WorkspaceLocation,
  type WorkspaceSearch,
} from '@/containers/agents/workspace-location.util';
import { WORKSPACE_PROVIDERS } from '@/containers/integrations/workspace-connection-card.component';
import { asRecord } from '@/components/agent-chat/as-record.util';

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

/** The options a `quick_replies` result offers, from its display or result. */
export function quickReplyOptions(display: unknown, result: unknown): string[] {
  const raw =
    asRecord(display)?.options ?? asRecord(result)?.options ?? undefined;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (option): option is string => typeof option === 'string' && !!option.trim()
  );
}

export interface PlayProposalView extends ProposalReviewState {
  proposalId: string;
  agentId: string | undefined;
  summary: string;
  added: number;
  changed: number;
  removed: number;
}

/** A `play_proposal` result: its id, agent, summary, change counts and issues. */
export function playProposalView(result: unknown): PlayProposalView | null {
  const data = asRecord(result);
  const proposalId = text(data?.proposal_id);
  if (!data || !proposalId) return null;
  const count = (value: unknown) => (Array.isArray(value) ? value.length : 0);
  const issues = Array.isArray(data.issues) ? data.issues : [];
  return {
    proposalId,
    agentId: text(data.agent_id),
    summary: text(data.summary) ?? '',
    buildState:
      data.buildState === 'blocked' || data.buildState === 'ready'
        ? data.buildState
        : null,
    canAccept: typeof data.canAccept === 'boolean' ? data.canAccept : undefined,
    issues: issues.flatMap((item) => {
      const issue = asRecord(item);
      const message = text(issue?.message);
      return issue && message
        ? [
            {
              code: text(issue.code) ?? '',
              message,
              nodeId: text(issue.nodeId),
              severity:
                issue.severity === 'error'
                  ? ('error' as const)
                  : ('warning' as const),
              blocks: issue.blocks === true,
            },
          ]
        : [];
    }),
    blockedReasons: (Array.isArray(data.blockedReasons)
      ? data.blockedReasons
      : []
    ).flatMap((item) => {
      const reason = asRecord(item);
      const message = text(reason?.message);
      return reason && message
        ? [
            {
              code: text(reason.code) ?? '',
              message,
              nodeId: text(reason.nodeId),
            },
          ]
        : [];
    }),
    requirementRevision: text(data.requirementRevision) ?? null,
    requirements: (Array.isArray(data.requirements)
      ? data.requirements
      : []
    ).flatMap((item) => {
      const requirement = asRecord(item);
      const id = text(requirement?.id);
      const label = text(requirement?.text);
      if (
        !requirement ||
        !id ||
        !label ||
        (requirement.state !== 'inferred_explicit' &&
          requirement.state !== 'user_confirmed') ||
        (requirement.coverage !== 'supported' &&
          requirement.coverage !== 'unverified')
      )
        return [];
      return [
        {
          id,
          text: label,
          state: requirement.state,
          coverage: requirement.coverage,
        },
      ];
    }),
    added: count(data.added),
    changed: count(data.changed),
    removed: count(data.removed),
  };
}

/**
 * "3 new · 1 changed · 1 removed", leaving out what is zero. A result with no
 * counts (the card shown again for a typed answer) reads the live proposal's.
 */
export function proposalChangesLine(
  view: PlayProposalView,
  live?: { added?: unknown[]; changed?: unknown[]; removed?: unknown[] }
): string {
  const fromLive = !view.added && !view.changed && !view.removed && live;
  const added = fromLive ? (live.added?.length ?? 0) : view.added;
  const changed = fromLive ? (live.changed?.length ?? 0) : view.changed;
  const removed = fromLive ? (live.removed?.length ?? 0) : view.removed;
  const parts: string[] = [];
  if (added) parts.push(`${added} new`);
  if (changed) parts.push(`${changed} changed`);
  if (removed) parts.push(`${removed} removed`);
  return parts.join(' · ');
}

/** Proposals the dock already switched the workspace to, once each. */
const shownProposals = new Set<string>();

/** True the first time a proposal asks to be shown, false after. */
export function claimProposalShow(proposalId: string): boolean {
  if (shownProposals.has(proposalId)) return false;
  shownProposals.add(proposalId);
  return true;
}

/** Test hook: forget which proposals were shown. */
export function resetShownProposals() {
  shownProposals.clear();
}

/** Whether the page is that agent's workspace. */
export function onAgentWorkspace(
  pathname: string,
  agentId: string | undefined
): boolean {
  return !!agentId && pathname.replace(/\/$/, '') === `/agents/${agentId}`;
}

/**
 * The search that opens a place in an agent's panel from a card in the chat
 * (a proposal, a run). On that agent's workspace the chat open in the dock
 * stays open, and so does `new`: without the chat the workspace clears it,
 * which cuts off the turn still streaming. From anywhere else the panel
 * opens on its own.
 */
export function panelSearch(
  previous: unknown,
  onWorkspace: boolean,
  location: WorkspaceLocation
): WorkspaceSearch {
  const current = onWorkspace
    ? parseWorkspaceSearch(asRecord(previous) ?? {})
    : {};
  return searchOf(location, current.new, current.chat);
}

export const CONNECTION_APP_NAME: Record<string, string> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  attio: 'Attio',
  slack: 'Slack',
  teams: 'Microsoft Teams',
  email: 'Email',
  linkedin: 'LinkedIn',
  lemlist: 'lemlist',
  outreach: 'Outreach',
  salesloft: 'Salesloft',
  nooks: 'Nooks AI',
  instantly: 'Instantly',
  notion: 'Notion',
  ...Object.fromEntries(
    Object.entries(WORKSPACE_PROVIDERS).map(([kind, { name }]) => [kind, name])
  ),
};

/** A workspace tool (Google, call notes, Notion, Airtable) Clu's card offers. */
export function isWorkspaceKind(
  kind: string
): kind is keyof typeof WORKSPACE_PROVIDERS {
  return Object.prototype.hasOwnProperty.call(WORKSPACE_PROVIDERS, kind);
}

/** The outside sequencers, whose connection Clu's card reads live. */
export const SEQUENCER_CONNECTION_KINDS: readonly string[] = [
  'lemlist',
  'outreach',
  'salesloft',
  'nooks',
  'instantly',
];

/**
 * Where the card's button connects the app: the integrations page, which
 * sends `connect=email` on to the sender connections with the provider
 * picker open (email accounts moved to the sequencing settings), and scrolls
 * to the tool's own card (`focus`) for a workspace tool or an outside
 * sequencer.
 */
export function connectionHref(kind: string): string {
  if (kind === 'email') return '/integrations?connect=email';
  return kind === 'teams' ||
    isWorkspaceKind(kind) ||
    SEQUENCER_CONNECTION_KINDS.includes(kind)
    ? `/integrations?focus=${kind}`
    : '/integrations';
}

/** The app a `connection_required` result names, and whether it is connected. */
export function connectionOf(
  display: unknown,
  result: unknown
): { kind: string; name: string; connected: boolean } | null {
  const shown = asRecord(display) ?? {};
  const data = asRecord(result) ?? {};
  const kind = text(shown.kind) ?? text(data.kind);
  if (!kind) return null;
  return {
    kind,
    name: CONNECTION_APP_NAME[kind] ?? kind,
    connected: (shown.connected ?? data.connected) === true,
  };
}

/** One app a run stopped on: connect it, or pick the member's own mailbox
 *  for the play's Send an email steps (`nodeIds`). */
export interface ConnectionNeed {
  kind: string;
  name: string;
  connected: boolean;
  reason: 'connect' | 'pick_mailbox';
  nodeIds: string[];
}

/**
 * Every app a `connection_required` result names. A failed Try or dry run
 * sends `connections`, one entry per app it stopped on, with the agent and
 * play the mailbox pick saves to; a card for one app sends only `kind`. An
 * entry without a kind is skipped. `listed` is true for the run's list.
 */
export function connectionsOf(
  display: unknown,
  result: unknown
): {
  needs: ConnectionNeed[];
  listed: boolean;
  agentId: string | undefined;
  playbookId: string | undefined;
} | null {
  const shown = asRecord(display) ?? {};
  const data = asRecord(result) ?? {};
  const agentId = text(shown.agent_id) ?? text(data.agent_id);
  const playbookId = text(shown.playbook_id) ?? text(data.playbook_id);
  const listed = Array.isArray(shown.connections)
    ? shown.connections
    : Array.isArray(data.connections)
      ? data.connections
      : null;
  if (listed) {
    const needs = listed.flatMap((entry): ConnectionNeed[] => {
      const row = asRecord(entry);
      const kind = text(row?.kind);
      if (!row || !kind) return [];
      return [
        {
          kind,
          name: CONNECTION_APP_NAME[kind] ?? kind,
          connected: row.connected === true,
          reason: row.reason === 'pick_mailbox' ? 'pick_mailbox' : 'connect',
          nodeIds: Array.isArray(row.node_ids)
            ? row.node_ids.filter(
                (id): id is string => typeof id === 'string' && id !== ''
              )
            : [],
        },
      ];
    });
    if (needs.length > 0) return { needs, listed: true, agentId, playbookId };
  }
  const single = connectionOf(display, result);
  if (!single) return null;
  return {
    needs: [{ ...single, reason: 'connect', nodeIds: [] }],
    listed: false,
    agentId,
    playbookId,
  };
}

/**
 * The page a failed run's fix is made on (a reconnect, a top-up), from the
 * `run_diagnosis` display. Only a path on this app: the card opens it in a new
 * tab, so a value that is not one is dropped.
 */
export function diagnosisLinkOf(
  display: unknown,
  result: unknown
): { url: string; label: string } | null {
  const link =
    asRecord(asRecord(display)?.link) ?? asRecord(asRecord(result)?.link);
  const url = text(link?.url);
  const label = text(link?.label);
  if (!url || !label || !url.startsWith('/') || url.startsWith('//'))
    return null;
  return { url, label };
}

/** The run an `agent_test_started` result started, and its agent. */
export function testRunOf(
  display: unknown,
  result: unknown
): { runId: string; agentId: string | undefined } | null {
  const shown = asRecord(display) ?? {};
  const data = asRecord(result) ?? {};
  const runId = text(shown.run_id) ?? text(data.run_id);
  if (!runId) return null;
  return { runId, agentId: text(shown.agent_id) ?? text(data.agent_id) };
}
